(ns manage.adset-decision-tree-page.view.side-panel.editors
  (:require ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/InputAdornment$default" :as InputAdornment]
            ["@mui/material/Tooltip$default" :as Tooltip]
            [clojure.string :as str]
            [common.form :as form]
            [common.widgets.form-field :refer [form-field]]
            [manage.adset-decision-tree-page.property :as property]
            [manage.adset-decision-tree-page.selection :as selection]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.view.common
             :refer [meta-adset-autocomplete-editor tiktok-ad-group-autocomplete-editor]]
            [manage.adset-decision-tree-page.view.hook :as hook]
            [manage.adset-decision-tree-page.view.node-conversion
             :as node-conversion]
            [manage.adset-decision-tree-page.view.side-panel.property-form
             :refer [get-property-field-name use-property-value use-set-property-value]]
            [manage.adset-decision-tree-page.view.side-panel.util
             :as side-panel.util]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defn get-property-value [node-data selected-item property]
  (let [base-path (selection/get-selected-item-base-path node-data selected-item)
        data-path (cond->> (:data-path property)
                    (some? base-path) (into base-path))]
    (get-in node-data data-path)))

(defn get-closest-inheritance-details [node-id->node-data selected-item property]
  (let [node-id (selection/get-selected-node-id selected-item)
        {:keys [parent-id] node-type :type} (get node-id->node-data node-id)]
    (if (node-conversion/root-node-type? node-type)
      {:root-node true}
      (loop [node-id parent-id]
        (when (some? node-id)
          (let [{:keys [parent-id] :as node-data} (get node-id->node-data node-id)
                value (get-property-value node-data selected-item property)]
            (if (not (str/blank? value))
              {:inherited-value value
               :node-data node-data}
              (recur parent-id))))))))

(defhook use-inheritance [selected-item property]
  (let [node-id->node-data (use-subscribe [::sub/node-id->node-data])]
    (when node-id->node-data
      (get-closest-inheritance-details node-id->node-data selected-item property))))

(defui inherited-tag [{:keys [label]
                       node-type :type}]
  (let [inherited-from-label
        (if (node-conversion/root-node-type? node-type)
          "the Starting Point"
          (str label " (" (:name (node-conversion/->decision-node node-type)) ")"))]
    ($ Tooltip {:placement "bottom"
                :title (str "Value inherited from " inherited-from-label)}
       ($ Chip {:label "Inherited"}))))

(defui revert-button [{:keys [set-value]}]
  ($ Tooltip {:placement "bottom"
              :title "Revert to inherited value"}
     ($ IconButton {:on-click #(set-value nil)}
        ($ Icon "cancel"))))

(defui inherited-adornment [{:keys [value inherited-value node-data set-value]}]
  ($ InputAdornment {:position "end"}
     (cond
       (and (some? inherited-value)
            (not (str/blank? value))) ($ revert-button {:set-value set-value})
       (some? inherited-value) ($ inherited-tag node-data))))

(defui text-input
  [{:keys [form-control input-props property sx] {:keys [label required]} :property}]
  ($ form-field {:label label
                 :required? required}
     ($ form/form-text-field {:control (:control form-control)
                              :name (get-property-field-name property)
                              :field-props {:sx sx
                                            :slotProps #js {:input input-props}}})))

(defhook use-inherited-input-props
  [{:keys [form-control property]
    {:keys [inherited-value root-node] :as inheritance-details} :inheritance-details}]
  (let [value (use-property-value form-control property)
        end-adornment ($ inherited-adornment
                         (assoc inheritance-details
                                :value value
                                :set-value (use-set-property-value form-control property)))]
    {:placeholder inherited-value
     :endAdornment (when-not root-node end-adornment)}))

(defui inherited-text-input [props]
  (let [input-props (use-inherited-input-props props)]
    ($ text-input (assoc props
                         :input-props (clj->js input-props)
                         :sx #js {".MuiOutlinedInput-root" #js {:paddingRight "0.5rem"}}))))

(defui ad-variant-pixel-editor [props]
  (let [inherited-node-data (use-subscribe [::sub/root-node-data])
        root-as-selected-item (use-subscribe [::sub/root-as-selected-item])
        [[{inherited-value :value}]] (hook/use-selected-item-data-path
                                      {:selected-item root-as-selected-item
                                       :data-path property/meta-pixel-data-path})]
    ($ side-panel.util/collapsible-wrapper {:label "Pixel"
                                            :collapsed-label "Pixel (1)"}
       ($ inherited-text-input
          (assoc props :inheritance-details {:inherited-value inherited-value
                                             :node-data inherited-node-data})))))

(defn use-autocomplete-options [options]
  (uix/use-memo
   #(map (fn [value] {:label value
                      :value value})
         options)
   [options]))

(defui autocomplete-editor [{:keys [property form-control]}]
  ($ form-field
     {:label (:label property)
      :required? (:required property)}
     ($ form/autocomplete-field
        {:name (get-property-field-name property)
         :control (:control form-control)
         :field-props {:size "small"
                       :options (use-autocomplete-options (:options property))
                       :multi? (:multiple property)}})))

(defui lat-lon-editor [{:keys [property] {:keys [control]} :form-control}]
  ($ :<>
     ($ form-field
        {:label "Latitude"
         :required? (:required property)}
        ($ form/form-text-field
           {:name (get-property-field-name property :lat)
            :control control
            :on-change-fn #(str/replace % #"[^\d\.-]" "")
            :field-props {:size "small"}}))
     ($ form-field
        {:label "Longitude"
         :required? (:required property)}
        ($ form/form-text-field
           {:name (get-property-field-name property :lon)
            :control control
            :on-change-fn #(str/replace % #"[^\d\.-]" "")
            :field-props {:size "small"}}))))

(defui meta-adset-editor
  [{:keys [selected-item property form-control] :as props}]
  (let [inheritance-details (use-inheritance selected-item property)
        input-props (use-inherited-input-props
                     (assoc props :inheritance-details inheritance-details))]
    ($ meta-adset-autocomplete-editor
       {:name (get-property-field-name property)
        :label (:label property)
        :control (:control form-control)
        :inherited-input-props input-props})))

(defui tiktok-ad-groups-editor
  [{:keys [selected-item property form-control] :as props}]
  (let [inheritance-details (use-inheritance selected-item property)
        input-props (use-inherited-input-props
                     (assoc props :inheritance-details inheritance-details))]
    ($ tiktok-ad-group-autocomplete-editor
       {:name (get-property-field-name property)
        :label (:label property)
        :control (:control form-control)
        :inherited-input-props input-props})))
